import React, { useEffect, useState } from 'react';
import { SkipForward, RefreshCw } from 'lucide-react';

interface AutoPlayOverlayProps {
    seconds: number;
    isAutoplay?: boolean; // Nueva prop opcional (default true si no se pasa, aunque App siempre la pasa)
    onCancel: () => void; // Volver a reproducir
    onConfirm: () => void; // Continuar inmediatamente
    onComplete: () => void; // Tiempo agotado (acción automática)
    nextTitle?: string;
}

const AutoPlayOverlay: React.FC<AutoPlayOverlayProps> = ({
    seconds,
    isAutoplay = true,
    onCancel,
    onConfirm,
    onComplete,
    nextTitle = 'Siguiente lección',
}) => {
    const [timeLeft, setTimeLeft] = useState(seconds);
    const [animate, setAnimate] = useState(false);

    // Lógica del temporizador (Números y evento final)
    useEffect(() => {
        if (!isAutoplay) return;

        if (timeLeft === 0) {
            onComplete();
            return;
        }

        const timer = setTimeout(() => {
            setTimeLeft((prev) => prev - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [timeLeft, onComplete, isAutoplay]);

    // Activar animación visual al montar si es autoplay
    useEffect(() => {
        if (isAutoplay) {
            // Pequeño delay para asegurar que el estado inicial (vacío) se renderice antes de iniciar la transición
            const timer = setTimeout(() => {
                setAnimate(true);
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [isAutoplay]);

    // Cálculos para el círculo SVG
    const radius = 30;
    const circumference = 2 * Math.PI * radius;

    // Si es autoplay: empieza vacío (offset = circumference) y transiciona a lleno (offset = 0)
    // Si NO es autoplay: se muestra lleno estático (offset = 0)
    const strokeDashoffset = isAutoplay ? (animate ? 0 : circumference) : 0;

    return (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="flex flex-col items-center max-w-sm w-full p-6 text-center">
                <h3 className="text-white/60 text-sm font-medium uppercase tracking-widest mb-6">
                    {isAutoplay ? 'A continuación' : 'Lección Finalizada'}
                </h3>

                {/* Indicador Circular */}
                <div className="relative mb-8 group cursor-pointer" onClick={onConfirm}>
                    <svg className="w-24 h-24 transform -rotate-90">
                        {/* Círculo de fondo */}
                        <circle
                            cx="48"
                            cy="48"
                            r={radius}
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="transparent"
                            className="text-white/10"
                        />
                        {/* Círculo de progreso */}
                        <circle
                            cx="48"
                            cy="48"
                            r={radius}
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="transparent"
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                            strokeLinecap="round"
                            className="text-indigo-500"
                            style={{
                                transition: isAutoplay ? `stroke-dashoffset ${seconds}s linear` : 'none',
                            }}
                        />
                    </svg>

                    {/* Contenido central: Número si hay autoplay, Icono si no */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        {isAutoplay ? (
                            <>
                                <span className="text-3xl font-bold text-white group-hover:hidden">{timeLeft}</span>
                                <SkipForward className="w-8 h-8 text-white hidden group-hover:block fill-white" />
                            </>
                        ) : (
                            // Si no hay autoplay, mostramos el icono de Play/Next permanentemente
                            <SkipForward className="w-8 h-8 text-white fill-white" />
                        )}
                    </div>
                </div>

                <h2 className="text-xl font-bold text-white mb-8 truncate w-full">{nextTitle}</h2>

                {/* Botones de acción */}
                <div className="flex gap-4 w-full">
                    <button
                        onClick={onCancel}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors font-medium text-sm group"
                    >
                        <RefreshCw size={16} className="group-hover:-rotate-180 transition-transform duration-500" />
                        Volver a reproducir
                    </button>

                    <button
                        onClick={onConfirm}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors font-medium text-sm shadow-lg shadow-indigo-900/20"
                    >
                        Continuar
                        <SkipForward size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AutoPlayOverlay;
